import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { oidcUserManager } from '../../services/oidcService';
import { clearDesktopAssertion } from '../../services/desktopAssertion';
import { getSSOTenantsResult, loginWithSso, type Tenant } from '../../services/acceoConnectService';
import { useConnectCredentialsStore } from '../../stores/session/connectCredentialsStore';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { useLanguageStore } from '../../stores/languageStore';
import { syncService } from '../../services/syncService';
import { establishDesktopAuthSession, establishBrowserAppSession } from '../../services/appSession';
import { extractLaunchedIcIdentifiantPublique, extractLaunchedHighWaterMark, extractLaunchedLang, decodeDesktopLaunchToken } from '../../utils/bridge/desktopLaunchToken';
import { applyBridgeLaunchContext } from '../../services/bridgeLaunchContext';
import { useSyncStore } from '../../stores/syncStore';
import { TenantPicker } from '../../components/TenantPicker/TenantPicker';
import { TranslationKey } from '../../i18n/translationKeys';

export const SigninCallback: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setAcceoConnectToken, setSaasSessionToken } = useConnectCredentialsStore();
    const { setUser } = useAuthStore();
    const { setTenant, setDesktopTenant } = useTenantStore();
    const { seedServerPreference, setLang } = useLanguageStore();

    const [tenants, setTenants] = useState<Tenant[] | null>(null);
    const [connectUnavailable, setConnectUnavailable] = useState(false);
    const [isToken, setIsToken] = useState<string | null>(null);
    const called = useRef(false);

    // Step 1 — complete OIDC handshake and route to the appropriate session setup.
    useEffect(() => {
        if (called.current) return;
        called.current = true;

        void (async () => {
            try {
                const user = await oidcUserManager.signinCallback();
                if (!user) return;

                setUser({
                    name: user.profile.name ?? user.profile.email ?? '',
                    email: user.profile.email ?? '',
                });

                // Acomba Desktop launch: the assertion rides in the OIDC state carried through the
                // redirect round-trip. No AcceoConnect, no picker — single workspace resolved by the
                // backend against the ICIdentifiantPublique in the assertion.
                const desktopAssertion = (user.state as { src?: string } | undefined)?.src;
                if (desktopAssertion) {
                    await completeDesktopLogin(user.access_token, desktopAssertion);
                    return;
                }

                // AcombaGo / browser: AcceoConnect tenant-list flow. This is confirmed non-desktop,
                // so drop any stale desktop assertion from an earlier abandoned launch — otherwise a
                // later error-page retry could wrongly resume the desktop flow.
                clearDesktopAssertion();
                const { tenants: tenantList, connectUnavailable: partial } =
                    await getSSOTenantsResult(user.access_token);

                if (tenantList.length === 0) {
                    void navigate('/signin-error', { replace: true });
                    return;
                }

                // Auto-selecting the last tenant standing would hide the fact that others are missing:
                // the user lands in a company they did not choose, with no way to tell that the one they
                // wanted exists but could not be resolved. Show the picker so the warning has somewhere
                // to render, even for a single row.
                if (tenantList.length === 1 && !partial) {
                    await completeLogin(user.access_token, tenantList[0]);
                } else {
                    setIsToken(user.access_token);
                    setConnectUnavailable(partial);
                    setTenants(tenantList);
                }
            } catch (err) {
                console.error('Sign-in callback failed:', err);
                void navigate('/signin-error', { replace: true });
            }
        })();
    }, []);

    // Step 2 — called once the tenant is known (either auto-selected or picked).
    const setInitialSyncRequired = useSyncStore(s => s.setInitialSyncRequired);

    const completeLogin = async (accessToken: string, tenant: Tenant) => {
        try {
            // Renamed on the way in: the OAuth wire format is snake_case, the codebase is camelCase.
            const { access_token: connectToken, expires_in: expiresIn, saasSessionToken } =
                await loginWithSso(accessToken, tenant.tenantCode, tenant.username, tenant.ciu);
            setAcceoConnectToken(connectToken, expiresIn);
            setSaasSessionToken(saasSessionToken ?? null);
            setTenant(tenant.tenantCode, tenant.companyName, tenant.ciu);
            // Mint our app session now (the API bearer for every request) and mark the session 'browser'.
            // Doing it here — not lazily in useAuthGuard — means readiness is never gated on a mid-guard
            // mint, which is what bounced the browser flow back to the picker.
            if (!(await establishBrowserAppSession(accessToken, tenant.ciu, tenant.tenantCode))) {
                void navigate('/signin-error', { replace: true });
                return;
            }
            seedServerPreference();
            try {
                const status = await syncService.getStatus();
                if (!status.hasEverSynced) {
                    setInitialSyncRequired(true);
                }
            } catch {
                // Fail-open: hasEverSynced treated as true
            }
            void navigate('/', { replace: true });
        } catch (err) {
            console.error('AcceoConnect login failed:', err);
            void navigate('/signin-error', { replace: true });
        }
    };

    // Desktop branch: exchange the assertion for an app session via /connect/desktop-auth.
    // The backend resolves the Catalog workspace (ICIdentifiantPublique → workspace CIU),
    // provisions the tenant DB, and returns our app tokens. No AcceoConnect, no picker.
    const completeDesktopLogin = async (oidcAccessToken: string, assertion: string) => {
        const outcome = await establishDesktopAuthSession(oidcAccessToken, assertion);
        if (!outcome.ok) {
            // Surface "company not recognized" as the specific message; carry the assertion either way
            // so the error page's retry re-enters the desktop flow rather than the AcombaGo tenant
            // picker — which would drop the single-workspace desktop context.
            const reason = outcome.reason === 'no-company-access' ? 'no-company-access' : undefined;
            void navigate('/signin-error', { replace: true, state: { reason, src: assertion } });
            return;
        }

        const { result } = outcome;
        const launchedKey = extractLaunchedIcIdentifiantPublique(assertion);
        const launchedHighWaterMark = extractLaunchedHighWaterMark(assertion);
        // Persisted alongside icIdentifiantPublique/highWaterMark so the desktop fast path
        // (DesktopAuth.tsx) can also require the licence to match before silently reusing a session —
        // a licence transfer/re-registration can change licenseCIU while the société identifier stays
        // the same.
        const launchedLicenseCiu = decodeDesktopLaunchToken(assertion)?.licenseCIU ?? null;
        // Persisted so the desktop fast path (DesktopAuth.tsx, tryReuseDesktopSession) can also require
        // the Acomba Usager to match before silently reusing a session — without this, a different
        // Usager opening the app under the same société/licence would inherit the previous Usager's
        // session and access decision.
        const launchedUsername = decodeDesktopLaunchToken(assertion)?.username ?? null;
        setDesktopTenant(result.workspaceCiu, result.companyName, result.isStaleCopy, launchedKey, launchedHighWaterMark, launchedLicenseCiu, launchedUsername);
        // Acomba's own language wins over the locally detected/persisted one: the CEF browser reports
        // the Windows locale, so an English Acomba on a French Windows would otherwise open in French.
        // setLang persists server-side itself, which is what seedServerPreference does for the rest.
        const launchedLang = extractLaunchedLang(assertion);
        if (launchedLang) setLang(launchedLang);
        else seedServerPreference();
        applyBridgeLaunchContext(assertion);
        // Deliberately NOT cleared here: SignoutCallback.tsx reads this same stash back to carry the
        // desktop context through the *next* login, after the user logs out later in this session.
        // Clearing it here would defeat that — logout would then fall through to the AcombaGO tenant
        // picker, which has no meaning for a single-workspace Acomba Desktop launch. The TTL in
        // desktopAssertion.ts is what bounds its lifetime instead.
        // First-sync check — same as the AcombaGo path (completeLogin): a workspace that has never
        // synced must go through the InitialSync overlay (which runs the bridge Full sync) before Home.
        try {
            const status = await syncService.getStatus();
            if (!status.hasEverSynced) {
                setInitialSyncRequired(true);
            }
        } catch {
            // Fail-open: hasEverSynced treated as true
        }
        void navigate('/', { replace: true });
    };

    const handleTenantSelect = (tenant: Tenant) => {
        if (!isToken) return;
        void completeLogin(isToken, tenant);
    };

    if (tenants) {
        return (
            <TenantPicker
                tenants={tenants}
                onSelect={handleTenantSelect}
                connectUnavailable={connectUnavailable}
            />
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen">
            <p className="text-primary-600">{t(TranslationKey.Auth.SigninInProgress)}</p>
        </div>
    );
};
